import { useMemo, useState } from "react"
import { Badge, Button, Col, Row, Spinner } from "reactstrap"
import Select from "react-select"

import PageWrapper from "components/Common/PageWrapper"
import { useShowrooms } from "hooks/showroomHook"
import { usePrayerSchedule } from "hooks/prayerTimeHook"

const SEGMENT_LABELS = {
  fajr: "Fajr",
  sunrise: "Sunrise",
  duha: "Ishraq / Duha",
  dhuhr: "Dhuhr",
  asr: "Asr (Hanafi)",
  sunset: "Sunset",
  maghrib: "Maghrib",
  isha: "Isha",
  tahajjud: "Tahajjud",
}

const segmentLabel = key =>
  key.startsWith("forbidden") ? "Forbidden time (Makruh)" : SEGMENT_LABELS[key] ?? key

const ROW_CLASS = {
  prayer: "",
  sun: "table-warning",
  forbidden: "table-danger",
  optional: "table-light",
}

const rakatText = rakats =>
  (rakats ?? []).map(r => `${r.label} ${r.count}`).join(" · ")

const formatGregorian = dateStr => {
  if (!dateStr) return ""
  const d = new Date(`${dateStr}T00:00:00`)
  return d.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  })
}

const PrayerTimes = () => {
  document.title = "Prayer Times"

  const [showroomId, setShowroomId] = useState(null)
  const [date, setDate] = useState(null)

  const { data: showroomResp } = useShowrooms({ per_page: 200 })
  const showroomOptions = useMemo(() => {
    const list = showroomResp?.data?.data ?? showroomResp?.data ?? []
    return list.map(s => ({ value: s.id, label: s.name }))
  }, [showroomResp])

  const params = useMemo(() => {
    const p = {}
    if (showroomId) p.showroom_id = showroomId
    if (date) p.date = date
    return p
  }, [showroomId, date])

  // No showroom_id → backend defaults to the logged-in user's showroom.
  const { data: resp, isLoading, isFetching } = usePrayerSchedule(params, true)
  const schedule = resp?.data
  const activeShowroomId = showroomId ?? schedule?.showroom?.id ?? null

  const prayerRows = useMemo(
    () => (schedule?.segments ?? []).filter(s => s.type === "prayer"),
    [schedule]
  )

  return (
    <PageWrapper title="Prayer Times" breadcrumbItem="Prayer Times">
      <Row className="mb-3 align-items-end g-2">
        <Col md={5}>
          <label className="form-label">Showroom</label>
          <Select
            options={showroomOptions}
            value={showroomOptions.find(o => o.value === activeShowroomId) ?? null}
            onChange={opt => {
              setShowroomId(opt?.value ?? null)
              setDate(null)
            }}
            placeholder="Your showroom"
            isClearable
          />
          {!showroomId && schedule?.showroom && (
            <small className="text-muted">Showing your showroom by default.</small>
          )}
        </Col>
        {schedule && (
          <Col md="auto" className="d-flex align-items-center gap-2">
            <Button
              size="sm"
              color="light"
              onClick={() => setDate(schedule.prev_date)}
              disabled={isFetching}
            >
              <i className="mdi mdi-chevron-left" />
            </Button>
            <div className="text-center px-2">
              <div className="fw-semibold">{formatGregorian(schedule.date)}</div>
              {schedule.hijri && (
                <div className="small text-muted">{schedule.hijri.en}</div>
              )}
              {schedule.hijri?.ar && (
                <div className="small text-muted" dir="rtl">
                  {schedule.hijri.ar}
                </div>
              )}
              {date && (
                <Button
                  color="link"
                  size="sm"
                  className="p-0"
                  onClick={() => setDate(null)}
                >
                  Back to today
                </Button>
              )}
            </div>
            <Button
              size="sm"
              color="light"
              onClick={() => setDate(schedule.next_date)}
              disabled={isFetching}
            >
              <i className="mdi mdi-chevron-right" />
            </Button>
          </Col>
        )}
      </Row>

      {isLoading && (
        <div className="text-center py-5">
          <Spinner color="primary" size="sm" /> Loading schedule…
        </div>
      )}

      {!isLoading && schedule && !schedule.coordinates_available && (
        <div className="alert alert-warning">
          {schedule.showroom
            ? "This showroom has no coordinates. Add latitude/longitude (or geofence) in showroom settings to compute prayer times."
            : "You are not assigned to a showroom. Pick a showroom above to view its prayer schedule."}
        </div>
      )}

      {schedule?.coordinates_available && (
        <>
          {schedule.current && (
            <div className="mb-3">
              <Badge color="success" className="px-3 py-2">
                Now: {segmentLabel(schedule.current.key)} — ends {schedule.current.ends_at_12h}
              </Badge>
            </div>
          )}

          <div className="table-responsive mb-4">
            <table className="table table-nowrap align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Prayer</th>
                  <th>Waqt time</th>
                  <th>Jamaat</th>
                  <th>Rakats</th>
                </tr>
              </thead>
              <tbody>
                {(schedule.segments ?? []).map(seg => {
                  const isCurrent = schedule.current?.key === seg.key
                  return (
                    <tr
                      key={seg.key}
                      className={`${ROW_CLASS[seg.type] ?? ""} ${isCurrent ? "fw-semibold" : ""}`}
                    >
                      <td>
                        {segmentLabel(seg.key)}
                        {isCurrent && (
                          <Badge color="success" className="ms-2">
                            now
                          </Badge>
                        )}
                      </td>
                      <td>
                        {seg.end_12h ? `${seg.start_12h} – ${seg.end_12h}` : seg.start_12h}
                      </td>
                      <td>{seg.jamaat_12h ?? "—"}</td>
                      <td className="text-muted">{rakatText(seg.rakats) || "—"}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <h6 className="fw-semibold mb-2">Five daily prayers — rakat breakdown</h6>
          <Row className="g-2">
            {prayerRows.map(seg => (
              <Col md={6} lg={4} key={`rakat-${seg.key}`}>
                <div className="border rounded p-3 h-100">
                  <div className="fw-semibold mb-1">{segmentLabel(seg.key)}</div>
                  <div className="small text-muted mb-1">
                    {seg.end_12h ? `${seg.start_12h} – ${seg.end_12h}` : seg.start_12h}
                  </div>
                  {seg.jamaat_12h && (
                    <div className="small text-info mb-2">Jamaat: {seg.jamaat_12h}</div>
                  )}
                  <div className="d-flex flex-wrap gap-1">
                    {(seg.rakats ?? []).map((r, i) => (
                      <Badge key={i} color="light" className="text-dark border">
                        {r.label}: {r.count}
                      </Badge>
                    ))}
                  </div>
                </div>
              </Col>
            ))}
          </Row>

          <p className="text-muted small mt-3 mb-0">
            Times from the Aladhan API (University of Islamic Sciences, Karachi method,
            Hanafi). Forbidden (makruh) windows and Duha/Tahajjud are derived from
            sunrise, zawal and sunset.
          </p>
        </>
      )}
    </PageWrapper>
  )
}

export default PrayerTimes
